import { Languages, LogOut, Settings2, UserRound } from 'lucide-react';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { ChevronIcon, Menu } from '../../merchant/ui/primitives.js';
import { useSession } from '../../workspace/session.js';

/** One account trigger, without changing session or role-routing behavior. */
export function MerchantAccountMenu({
  onNavigate,
}: {
  readonly onNavigate: () => void;
}): ReactNode {
  const { language, toggle } = useLanguage();
  const copy = workspaceCopy(language);
  const { session, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    trigger.current?.focus();
  }, []);
  const displayName =
    session.status === 'authenticated' ? session.account.displayName : '';
  const initial = displayName
    .trim()
    .slice(0, 1)
    .toLocaleUpperCase(language === 'ar' ? 'ar-SA' : 'en-US');
  const go = (path: string) => {
    close();
    onNavigate();
    if (path.startsWith('/settings') && !location.pathname.startsWith('/settings')) {
      const query = new URLSearchParams(location.search);
      query.set('settings', path.split('/')[2] ?? 'account');
      void navigate(`${location.pathname}?${query}`);
    } else void navigate(path);
  };

  return (
    <div
      className="mj-merchant-account-menu"
      onKeyDown={(event) => {
        if (!open || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key))
          return;
        const items = Array.from(
          event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
        );
        if (items.length === 0) return;
        event.preventDefault();
        const index = items.indexOf(document.activeElement as HTMLButtonElement);
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) %
                items.length;
        items[next]?.focus();
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="mj-merchant-sidebar-account"
        aria-label={`${copy.nav.account}: ${displayName || copy.nav.account}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={displayName || copy.nav.account}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <span className="mj-merchant-sidebar-account-mark" aria-hidden="true">
          {initial || <UserRound strokeWidth={1.8} />}
        </span>
        <span className="mj-merchant-sidebar-account-copy mj-merchant-sidebar-label">
          <span>{displayName || copy.nav.account}</span>
          <small>{copy.nav.account}</small>
        </span>
        <span className="mj-merchant-identity-chevron">
          <ChevronIcon />
        </span>
      </button>
      <Menu
        open={open}
        onClose={close}
        label={copy.nav.account}
        items={[
          {
            key: 'account',
            label: copy.nav.account,
            icon: <UserRound aria-hidden="true" />,
            onSelect: () => {
              go('/settings/account');
            },
          },
          {
            key: 'settings',
            label: copy.nav.settings,
            icon: <Settings2 aria-hidden="true" />,
            onSelect: () => {
              go('/settings');
            },
          },
          {
            key: 'language',
            label: (
              <span lang={language === 'ar' ? 'en' : 'ar'}>{copy.nav.language}</span>
            ),
            icon: <Languages aria-hidden="true" />,
            onSelect: () => {
              toggle();
              close();
            },
          },
          {
            key: 'logout',
            label: copy.nav.signOut,
            icon: <LogOut aria-hidden="true" />,
            onSelect: () => {
              close();
              onNavigate();
              void logout().finally(() => {
                void navigate('/', { replace: true });
              });
            },
          },
        ]}
      />
    </div>
  );
}
